/**
 * V2 Export my yoinks (2026-09-21) — the whole journal as a file the
 * explorer keeps, no Yoink needed to open it. The community research
 * (docs/COMMUNITY-RESEARCH-2026-07.md, Q6) names CSV export a trust
 * signal for the parks audience: Chimani locked lifetime members out,
 * and the paper passport's superpower is that it can't be discontinued.
 *
 *   yoinksCsv(rows)              one row per yoink; opens in Numbers,
 *                                Excel, Google Sheets
 *   yoinksKml(rows, { title })   one Placemark per yoink with
 *                                coordinates; opens in Google Earth,
 *                                Google My Maps, QGIS
 *   deliverFile({ name, text, type })
 *                                share sheet where there is one (iPad
 *                                and iPhone Safari: "Save to Files",
 *                                Mail, Numbers…), plain download where
 *                                there isn't
 *
 * Rows are the profile's FEED_SELECT shape (fetchAllUserYoinks): the
 * embedded `locations` row carries the place, `categories` the site
 * category, `personalShelf` the personal category. Photos are LINKED
 * (public storage URLs), not bundled — a photo archive is its own job.
 *
 * `created_at` is the VISIT date (CLAUDE.md, migration 20260915b) and is
 * the one that belongs in someone's own record; `inserted_at` is ours.
 */
import { imageSrc } from '../../core/photo.js';

/** RFC-4180 cell: quote when the value holds a comma, quote or newline. */
export function csvCell(val) {
  if (val === null || val === undefined) return '';
  const str = String(val);
  return /[",\r\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
}

/** XML text/attribute escaping — KML is XML, and place names hold `&`. */
export function xmlEscape(val) {
  return String(val ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/** The visit date as YYYY-MM-DD; a bad date exports as an empty cell. */
export function visitDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

/** Site category name, else the personal shelf's, else Uncategorized. */
export function categoryName(yoink) {
  return yoink.categories?.name || yoink.personalShelf?.name || 'Uncategorized';
}

export function photoUrls(yoink) {
  const images = Array.isArray(yoink.images) ? yoink.images : [];
  return images.map(imageSrc).filter(Boolean);
}

function yoinkUrl(yoink) {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const basePath = (typeof window !== 'undefined' && window.YOINK_CONFIG?.basePath) || '/adventure';
  return `${origin}${basePath}/yoink/${yoink.id}`;
}

function coords(yoink) {
  const loc = yoink.locations || {};
  // Number(null) is 0 — a place with no coordinates must not land in
  // the Gulf of Guinea, so test for absence before coercing.
  if (loc.lat == null || loc.lng == null || loc.lat === '' || loc.lng === '') return null;
  const lat = Number(loc.lat);
  const lng = Number(loc.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

/** Newest visit first, like the journal; an unparseable date sorts last. */
function sorted(rows) {
  const t = (y) => {
    const ms = new Date(y.created_at || '').getTime();
    return Number.isNaN(ms) ? -Infinity : ms;
  };
  return [...(rows || [])].sort((a, b) => t(b) - t(a));
}

export const CSV_HEADER = [
  'Date', 'Title', 'Place', 'Address', 'Latitude', 'Longitude',
  'County', 'State', 'Country', 'Category', 'Notes', 'Photos', 'Yoink URL',
];

export function yoinksCsv(rows) {
  const lines = sorted(rows).map((y) => {
    const loc = y.locations || {};
    const c = coords(y);
    return [
      visitDate(y.created_at),
      y.title,
      loc.display_name,
      loc.formatted_address,
      c ? c.lat : '',
      c ? c.lng : '',
      loc.county,
      loc.state_province,
      loc.country,
      categoryName(y),
      y.description,
      photoUrls(y).join(' '),
      yoinkUrl(y),
    ].map(csvCell).join(',');
  });
  // BOM so Excel on Windows reads the UTF-8 (café, Zürich) instead of
  // mojibake; Numbers and Sheets ignore it.
  return `\uFEFF${[CSV_HEADER.join(','), ...lines].join('\r\n')}\r\n`;
}

/**
 * KML 2.2. A yoink whose place has no coordinates has nowhere to stand on
 * a map, so it is left out — the CSV still carries it. Placemarks are
 * grouped in one Folder per category so Google Earth's sidebar reads
 * like the journal's stacks.
 */
export function yoinksKml(rows, { title = 'My yoinks' } = {}) {
  const byCategory = new Map();
  for (const y of sorted(rows)) {
    if (!coords(y)) continue;
    const name = categoryName(y);
    if (!byCategory.has(name)) byCategory.set(name, []);
    byCategory.get(name).push(y);
  }
  const folders = [...byCategory.entries()].map(([name, list]) => {
    const marks = list.map((y) => {
      const loc = y.locations || {};
      const c = coords(y);
      const lines = [
        loc.display_name,
        loc.formatted_address,
        visitDate(y.created_at) ? `Visited ${visitDate(y.created_at)}` : '',
        y.description,
        ...photoUrls(y),
        yoinkUrl(y),
      ].filter(Boolean);
      return [
        '      <Placemark>',
        `        <name>${xmlEscape(y.title || loc.display_name || 'Yoink')}</name>`,
        `        <description>${xmlEscape(lines.join('\n'))}</description>`,
        visitDate(y.created_at) ? `        <TimeStamp><when>${visitDate(y.created_at)}</when></TimeStamp>` : '',
        // KML is lng,lat — the opposite of every other column here
        `        <Point><coordinates>${c.lng},${c.lat},0</coordinates></Point>`,
        '      </Placemark>',
      ].filter(Boolean).join('\n');
    });
    return [
      '    <Folder>',
      `      <name>${xmlEscape(name)}</name>`,
      ...marks,
      '    </Folder>',
    ].join('\n');
  });
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<kml xmlns="http://www.opengis.net/kml/2.2">',
    '  <Document>',
    `    <name>${xmlEscape(title)}</name>`,
    ...folders,
    '  </Document>',
    '</kml>',
    '',
  ].join('\n');
}

/**
 * Hand a text file to the explorer. The share sheet is the door that
 * works on iPad and iPhone (Web Share Level 2, the same call the share
 * cards use to hand photos to Instagram): Files, Mail, Numbers, Drive.
 * Where there is no share sheet, or it refuses (Safari drops the tap's
 * activation if too much async work ran first — NotAllowedError), the
 * plain anchor download takes over.
 *
 * Returns 'shared' | 'downloaded' | 'dismissed'.
 */
export async function deliverFile({ name, text, type }) {
  const blob = new Blob([text], { type });
  if (typeof File === 'function' && navigator.canShare) {
    let file = null;
    try { file = new File([blob], name, { type }); } catch { /* no File ctor → download */ }
    if (file && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: name });
        return 'shared';
      } catch (err) {
        if (err?.name === 'AbortError') return 'dismissed';
        // NotAllowedError etc. — fall through to the download rung
      }
    }
  }
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoke on a delay: Safari starts the download after click() returns
  // and a synchronous revoke hands it a dead URL.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return 'downloaded';
}
